import { useTranslation } from "react-i18next";
import { Table, Pager, TableColumn, TableSort } from "@foris/avocado-suite";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { LinkLogEntryOrderByFieldsEnum, OrderByDirection } from "@models/ISchema";
import { LinkLogEntry } from "@models/ISchema";
import {
  getLinkCell,
  getLinkDisabledCell,
  getColumnCell,
  capitalizeFirstLetter,
  getColumnCellList,
  getUserCell,
} from "@common/utils/table.util";
import styles from "./linkHistoryTable.module.scss";

const MOPS_SOURCE = "Carga Masiva";

type LinkHistoryOrderBy = {
  header: string;
  field: string;
  direction: OrderByDirection;
};

type LinkHistorySearchBy = {
  fields: string[];
  text: string;
};

/**
 * Props interface for the LinkHistoryTable component
 */
interface LinkHistoryTableProps {
  /** Array of link history entries to display in the table */
  data: LinkLogEntry[];
  /** Sorting applied to the displayed entries */
  orderBy: LinkHistoryOrderBy;
  /** Search criteria applied to the displayed entries */
  searchBy: LinkHistorySearchBy;
  /** Pagination state containing page, size, and total count */
  pagination?: any;
  /** Configuration object for searchable columns */
  columnsToSearchBy?: {
    Fecha: boolean;
    Liga: boolean;
    "Recurso ID": boolean;
    Responsable: boolean;
  };
  /** Callback to update searchable columns configuration */
  setColumnsToSearchBy?: (columns: any) => void;
  /**
   * Callback triggered when table data needs to be reloaded
   * @param page - Page number to load
   * @param newOrderBy - New sorting configuration
   * @param searchBy - Search criteria
   */
  onLoadLogEntries?: (
    page: number,
    newOrderBy: LinkHistoryOrderBy,
    searchBy: LinkHistorySearchBy,
  ) => void;
  /** Callback when user changes the number of items per page */
  onChangeSizePerPage?: (value: number) => void;
}

/**
 * LinkHistoryTable displays a paginated table of link modification history
 *
 * Handles sorting, pagination, and rendering of link change entries with type-safe
 * rendering functions for each column. Provides navigation to the link editor
 * for enabled links and visual indicators for disabled links.
 */
const LinkHistoryTable = ({
  data = [],
  orderBy,
  searchBy,
  onLoadLogEntries,
  pagination,
  onChangeSizePerPage,
}: LinkHistoryTableProps) => {
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();
  const handleChangeSizePerPage = (value: number) => {
    onChangeSizePerPage?.(value);
  };

  /**
   * Table column definitions with type-safe rendering functions
   * Each column uses the appropriate rendering utility based on data type
   */
  const columns = [
    {
      key: LinkLogEntryOrderByFieldsEnum.Date,
      header: t("link-changes-history.table.date"),
      renderCell: (data: LinkLogEntry) => getColumnCell(data?.date),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "161px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.LinkCode,
      header: t("link-changes-history.table.link"),
      renderCell: (data: LinkLogEntry) => {
        const entry = data as LinkLogEntry;
        const isLinkEnabled = entry?.link?.enabled;
        return isLinkEnabled
          ? getLinkCell(editorUrl("link", entry?.link?.id), null, entry?.link?.code)
          : getLinkDisabledCell(entry?.link?.code);
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "180px" },
      },
    },
    {
      header: t("link-changes-history.table.resource"),
      renderCell: (data: LinkLogEntry) => getColumnCell(capitalizeFirstLetter(data?.resource)),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "165px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.ObjectId,
      header: t("link-changes-history.table.object-id"),
      renderCell: (data: LinkLogEntry) => getColumnCell(data?.objectId ?? "-"),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "157px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.ResourceChanges,
      header: t("link-changes-history.table.change-type"),
      renderCell: (data: LinkLogEntry) => getColumnCellList(data?.changeType),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "180px" },
      },
    },
    {
      header: t("link-changes-history.table.action"),
      renderCell: (data: LinkLogEntry) => getColumnCellList(data?.change),
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.UserId,
      header: t("link-changes-history.table.user"),
      renderCell: (data: LinkLogEntry) => {
        const isMopsSource = data?.source === MOPS_SOURCE;

        return getUserCell(data?.user, {
          icon: isMopsSource ? "settings" : "user",
          name: isMopsSource
            ? t("link-changes-history.table.massive-operations-system")
            : data?.user?.name,
        });
      },
      isSortable: true,
    },
  ];

  /**
   * Handles column header clicks for sorting
   * Requests the first page with the new sort parameters
   */
  const onHeaderClick = (column: TableSort) => {
    const newOrderBy = {
      direction: column.direction.toUpperCase() as OrderByDirection,
      field: column.column as any,
      header: "" as any,
    };

    onLoadLogEntries?.(1, newOrderBy, searchBy);
  };

  return (
    <div className={styles.linkHistoryTable}>
      <Table
        columns={columns as TableColumn[]}
        data={data}
        desktopVariant="regular"
        onSortChange={onHeaderClick}
        initialSort={
          {
            column: orderBy?.field,
            direction: orderBy?.direction?.toLocaleLowerCase() ?? "desc",
          } as TableSort
        }
      />

      {!!pagination?.total && (
        <Pager
          onChange={(page: number) => {
            onLoadLogEntries?.(page, orderBy, searchBy);
          }}
          showRowsPerPage={!!onChangeSizePerPage}
          pageSizeOptions={[10, 20]}
          onRowsPerPageChange={(size: { label: string; value: number }) =>
            handleChangeSizePerPage?.(size.value)
          }
          page={pagination?.page ?? 1}
          total={pagination?.total ?? 0}
          size={pagination?.size ?? 10}
          labelRowsPerPage={t("link-changes-history.pager.label-rows-per-page")}
          classname={{ global: styles.linkHistoryTable_pagerContainer }}
        />
      )}
    </div>
  );
};

export default LinkHistoryTable;
